import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const LandingPage = () => {
  const navigate = useNavigate();
  const [activePopups, setActivePopups] = useState([]);
  const [popupCounter, setPopupCounter] = useState(0);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  
  // Counter animation states
  const [anfragen, setAnfragen] = useState(0);
  const [klicks, setKlicks] = useState(0);
  
  // Timeline scroll animation state
  const [timelineHeight, setTimelineHeight] = useState(0);

  // Popup-Typen für die Hero Section
  const popupTypes = [
    {
      mainText: 'Installateur Wien',
      subText: '58 Anrufe durch Google Suchanzeige',
      iconType: 'phone'
    },
    {
      mainText: 'KFZ Linz',
      subText: 'Neue Anzeige gestartet',
      iconType: 'rocket'
    },
    {
      mainText: 'Friseur Graz',
      subText: 'Hohe Nachfrage',
      iconType: 'trend'
    },
    {
      mainText: 'Zahnarzt Salzburg',
      subText: 'Neue Anzeige gestartet',
      iconType: 'rocket'
    },
    {
      mainText: 'Elektriker Wien',
      subText: '42 Anrufe diese Woche',
      iconType: 'phone'
    }
  ];

  useEffect(() => {
    const showPopup = () => {
      // Clear any existing popups first
      setActivePopups([]);
      
      const randomPopup = popupTypes[Math.floor(Math.random() * popupTypes.length)];
      const newPopup = {
        id: popupCounter,
        ...randomPopup
      };

      // Show new popup after a short delay
      setTimeout(() => {
        setActivePopups([newPopup]);
      }, 500);

      setPopupCounter(prev => prev + 1);

      // Remove popup after 4 seconds
      setTimeout(() => {
        setActivePopups([]);
      }, 4500);
    };

    // Show first popup immediately
    showPopup();
    
    // Then show a new popup every 6 seconds (4.5s visible + 1.5s pause)
    const interval = setInterval(showPopup, 6000);

    return () => clearInterval(interval);
  }, []);

  // Counter animation for dashboard numbers
  useEffect(() => {
    const duration = 6000; // 6 seconds - very slow and smooth
    const steps = 180; // Even more steps for ultra-smooth animation
    const increment1 = 127 / steps;
    const increment2 = 2341 / steps;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      
      // Smoother easing function (ease-out with less acceleration)
      const progress = currentStep / steps;
      const easeProgress = 1 - Math.pow(1 - progress, 4); // quartic ease-out for smoother feel
      
      setAnfragen(Math.floor(127 * easeProgress));
      setKlicks(Math.floor(2341 * easeProgress));

      if (currentStep >= steps) {
        setAnfragen(127);
        setKlicks(2341);
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, []);

  // Timeline scroll animation
  useEffect(() => {
    const handleScroll = () => {
      const section = document.querySelector('#how-it-works');
      if (!section) return;

      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const scrollPosition = window.scrollY + window.innerHeight;
      const sectionBottom = sectionTop + sectionHeight;

      // Calculate progress through the section
      if (scrollPosition > sectionTop && window.scrollY < sectionBottom) {
        const progress = Math.min(
          Math.max((scrollPosition - sectionTop - 300) / (sectionHeight - 600), 0),
          1
        );
        setTimelineHeight(progress * 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const getIcon = (type) => {
    switch(type) {
      case 'phone':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        );
      case 'rocket':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        );
      case 'trend':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        .nav-link {
          position: relative;
          display: inline-block;
          padding-bottom: 4px;
        }
        
        .nav-link::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 0;
          height: 2px;
          background: rgba(91, 63, 255, 0.4);
          transition: width 0.4s ease;
        }
        
        .nav-link:hover::after {
          width: 100%;
        }
      `}</style>
      
      <div className="min-h-screen md:h-screen md:snap-y md:snap-mandatory md:overflow-y-scroll" style={{ backgroundColor: '#F9F7F4' }}>
        {/* Navigation - Desktop only sticky, Mobile scrolls */}
        <div className="md:fixed md:top-0 w-full z-50 flex justify-center pt-4 md:pt-6 px-4 md:px-6">
          <nav className="max-w-4xl w-full" style={{
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(30px) saturate(200%)',
            WebkitBackdropFilter: 'blur(30px) saturate(200%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
            borderRadius: '50px'
          }}>
            <div className="px-6 md:px-8 lg:px-10">
              <div className="flex justify-between items-center h-14 md:h-16">
                {/* Logo - Always Visible */}
                <div className="flex items-center space-x-2 md:space-x-3">
                  <img 
                    src="/logo.png" 
                    alt="AdSimplify Logo" 
                    className="h-6 w-6 md:h-8 md:w-8 object-contain"
                  />
                  <span className="text-xl md:text-2xl font-bold" style={{ color: '#1A1A1A' }}>
                    AdSimplify
                  </span>
                </div>
                
                {/* Desktop Navigation Links */}
                <div className="hidden md:flex items-center space-x-8">
                  <a 
                    href="#how-it-works" 
                    className="nav-link text-gray-700 hover:text-gray-900 transition text-base font-medium"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#how-it-works')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    So funktioniert's
                  </a>
                  <a 
                    href="#faq" 
                    className="nav-link text-gray-700 hover:text-gray-900 transition text-base font-medium"
                    onClick={(e) => {
                      e.preventDefault();
                      document.querySelector('#faq')?.scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    FAQ
                  </a>
                  <button
                    onClick={() => navigate('/login?mode=register')}
                    className="px-6 py-3 font-semibold text-white transition-all duration-200 hover:shadow-lg text-base"
                    style={{ 
                      backgroundColor: '#0040FF',
                      border: 'none',
                      borderRadius: '50px'
                    }}
                  >
                    Jetzt starten
                  </button>
                </div>
                
                {/* Mobile CTA Button */}
                <div className="md:hidden">
                  <button
                    onClick={() => navigate('/login?mode=register')}
                    className="px-5 py-2.5 font-semibold text-white transition-all duration-200 text-sm"
                    style={{ 
                      backgroundColor: '#0040FF',
                      border: 'none',
                      borderRadius: '50px'
                    }}
                  >
                    Starten
                  </button>
                </div>
              </div>
            </div>
          </nav>
        </div>

      {/* Hero Section */}
      <section className="pt-6 md:pt-32 pb-12 md:pb-24 px-4 md:px-6 lg:px-8 relative overflow-hidden md:snap-start md:snap-always min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          {/* Made in Österreich Badge */}
          <div className="flex justify-center mb-6 md:mb-8">
            <div className="text-xs md:text-sm text-gray-600 font-medium">
              Made with ❤️ in Österreich.
            </div>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-4 md:space-y-6 lg:space-y-8">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: '#0040FF' }}>
                  AdSimplify
                </h1>
                <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight" style={{ color: '#1A1A1A' }}>
                  Werde sichtbar, wenn Kunden deine Dienstleistung suchen.
                </h2>
              </div>
              
              {/* Two Badge Boxes */}
              <div className="flex flex-wrap gap-2 md:gap-3">
                <div className="px-3 md:px-4 py-2 md:py-2.5 rounded-xl text-sm md:text-base font-medium" style={{ backgroundColor: '#F3F4F6', color: '#1F2937' }}>
                  Keine Bindung
                </div>
                <div className="px-3 md:px-4 py-2 md:py-2.5 rounded-xl text-sm md:text-base font-medium" style={{ backgroundColor: '#F3F4F6', color: '#1F2937' }}>
                  Mehr Anfragen.
                </div>
              </div>
              
              <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
                Du sagst uns, was du anbietest. Wir kümmern uns um den Rest.<br />
                AdSimplify macht's einfach
              </p>
              
              <div className="space-y-3 md:space-y-4">
                <button
                  onClick={() => navigate('/login?mode=register')}
                  className="w-full md:w-auto px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-white text-base md:text-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                  style={{ backgroundColor: '#0040FF' }}
                >
                  Anzeige starten
                </button>
                <p className="text-xs md:text-sm text-gray-500">In 3 Minuten startklar</p>
              </div>
            </div>

            {/* Right: Dashboard Mockup with Popups */}
            <div className="relative">
              {/* Dashboard Mockup */}
              <div className="bg-white rounded-3xl p-8 shadow-2xl border border-gray-100 relative overflow-visible">
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold" style={{ color: '#1A1A1A' }}>Suchanzeigen-Übersicht</h3>
                    <div className="px-3 py-1 rounded-lg text-xs font-medium" style={{ backgroundColor: '#F0EDFF', color: '#5B3FFF' }}>
                      Live
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 rounded-xl" style={{ backgroundColor: '#F9FAFB' }}>
                      <p className="text-sm text-gray-600 mb-1">Anfragen</p>
                      <p className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>{anfragen}</p>
                      <p className="text-xs mt-1" style={{ color: '#10B981', fontWeight: '600' }}>↑ +18% diese Woche</p>
                    </div>
                    <div className="p-4 rounded-xl" style={{ backgroundColor: '#F9FAFB' }}>
                      <p className="text-sm text-gray-600 mb-1">Klicks</p>
                      <p className="text-3xl font-bold" style={{ color: '#1A1A1A' }}>{klicks.toLocaleString()}</p>
                      <p className="text-xs mt-1" style={{ color: '#10B981', fontWeight: '600' }}>↑ +12% diese Woche</p>
                    </div>
                  </div>
                  
                  <div className="h-32 rounded-xl" style={{ backgroundColor: '#F9FAFB' }}>
                    <div className="h-full flex items-end justify-around p-4 space-x-2">
                      {[40, 65, 45, 80, 60, 90, 75].map((height, i) => (
                        <div
                          key={i}
                          className="flex-1 rounded-t-lg chart-bar"
                          style={{
                            backgroundColor: '#5B3FFF',
                            '--final-height': `${height}%`,
                            opacity: 0.1 + (i * 0.1),
                            animationDelay: `${i * 0.1}s`
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Popup container - fixed height on mobile to prevent layout shift */}
              <div className="h-32 lg:h-0 relative">
                {/* Animated Popups - Absolute positioned */}
                <div className="absolute top-4 left-1/2 -translate-x-1/2 lg:top-auto lg:left-auto lg:translate-x-0 lg:-bottom-4 lg:-right-4 flex justify-center">
                  {activePopups.map((popup) => (
                    <div
                      key={popup.id}
                      className="rounded-2xl p-3 border border-white/30"
                      style={{
                        minWidth: '280px',
                        maxWidth: '320px',
                        animation: 'fadeInOut 4s ease-in-out',
                        zIndex: 10,
                        background: 'rgba(255, 255, 255, 0.4)',
                        backdropFilter: 'blur(40px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(40px) saturate(180%)',
                        boxShadow: '0 8px 32px 0 rgba(0, 64, 255, 0.25), inset 0 1px 0 0 rgba(255, 255, 255, 0.5)'
                      }}
                    >
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: '#5B3FFF' }}>
                          {getIcon(popup.iconType)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#10B981' }}></div>
                            <p className="text-xs font-medium text-gray-500">LIVE</p>
                          </div>
                          <p className="font-semibold text-sm" style={{ color: '#1A1A1A' }}>{popup.mainText}</p>
                          <p className="text-xs text-gray-600">{popup.subText}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* Problem/Solution Section */}
      <section id="value" className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 relative overflow-hidden md:snap-start md:snap-always min-h-screen flex items-center" style={{
        background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)'
      }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <div className="inline-block px-4 md:px-5 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6 border-2 border-red-500 text-red-600 bg-white">
              Das Problem
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6" style={{ color: '#1A1A1A' }}>
              Google Werbung ist kompliziert
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              Agenturen sind teuer, unpersönlich und arbeiten mit unklaren Verträgen. Kleine Unternehmen bleiben dabei oft auf der Strecke.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16 lg:mb-20">
            {[
              {
                problem: 'Agenturen kosten zu viel',
                detail: 'Monatliche Gebühren und fehlende Transparenz sorgen dafür, dass du am Ende mehr zahlst als du gewinnst.',
                euroIcon: true
              },
              {
                problem: 'Selbst machen ist keine Option',
                detail: 'Zu technisch, zu komplex und voller Fallstricke. Wer es selbst versucht, verbrennt Zeit und Geld.',
                icon: (
                  <svg className="w-6 h-6 md:w-8 md:h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M11.25 5.337c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.036 1.007-1.875 2.25-1.875S15 2.34 15 3.375c0 .369-.128.713-.349 1.003-.215.283-.401.604-.401.959 0 .332.278.598.61.578 1.91-.114 3.79-.342 5.632-.676a.75.75 0 01.878.645 49.17 49.17 0 01.376 5.452.657.657 0 01-.66.664c-.354 0-.675-.186-.958-.401a1.647 1.647 0 00-1.003-.349c-1.035 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401.31 0 .557.262.534.571a48.774 48.774 0 01-.595 4.845.75.75 0 01-.61.61c-1.82.317-3.673.533-5.555.642a.58.58 0 01-.611-.581c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.035-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959a.641.641 0 01-.658.643 49.118 49.118 0 01-4.708-.36.75.75 0 01-.645-.878c.293-1.614.504-3.257.629-4.924A.53.53 0 005.337 15c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.036 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.369 0 .713.128 1.003.349.283.215.604.401.959.401a.656.656 0 00.659-.663 47.703 47.703 0 00-.31-4.82.75.75 0 01.83-.832c1.343.155 2.703.254 4.077.294a.64.64 0 00.657-.642z" />
                  </svg>
                )
              },
              {
                problem: 'Jeden Tag gehen dir Kunden verloren',
                detail: 'Während du nach Lösungen suchst, kaufen Menschen bei deiner Konkurrenz, weil sie dich nicht finden.',
                icon: (
                  <svg className="w-6 h-6 md:w-8 md:h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 000-1.5h-3.75V6z" clipRule="evenodd" />
                  </svg>
                )
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border-2 border-gray-200 hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl border-2 border-gray-200 flex items-center justify-center mb-4 md:mb-6 text-gray-500 bg-white">
                  {item.euroIcon ? (
                    <span className="text-2xl md:text-4xl font-bold">€</span>
                  ) : (
                    item.icon
                  )}
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3" style={{ color: '#1A1A1A' }}>
                  {item.problem}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-block px-4 md:px-5 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold mb-4 md:mb-6 border-2 border-green-500 text-green-600 bg-white">
              Die Lösung
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6" style={{ color: '#1A1A1A' }}>
              AdSimplify macht es einfach
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8 md:mb-12">
              Wir automatisieren deine Google Suchanzeigen und liefern dir Agenturniveau zum Bruchteil des Preises. Dazu bekommst du persönliche Betreuung und erreichst uns jederzeit per Anruf, E-Mail oder WhatsApp.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 max-w-6xl mx-auto">
            {[
              {
                title: 'KI erstellt deine Anzeigentexte',
                description: 'Unsere KI analysiert dein Geschäft und schreibt professionelle Anzeigentexte. Die eigentliche Optimierung deiner Suchanzeigen übernehmen unsere Experten im Hintergrund und holen das Maximum aus deinem Budget heraus.',
                features: ['KI Anzeigentexte', 'Expertenoptimierung', 'Automatisierte Abläufe']
              },
              {
                title: 'Du behältst die Kontrolle',
                description: 'Du siehst jederzeit, wohin dein Budget fließt. Du kannst alles sofort anpassen, pausieren oder fortsetzen. Keine Tricks, keine Abhängigkeit.',
                features: ['Echtzeit Dashboard', 'Klare Budget Kontrolle', 'Jederzeit kündbar', 'Persönliche Betreuung durch unser Team']
              }
            ].map((solution, index) => (
              <div key={index} className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 shadow-lg border border-gray-100">
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-2xl border-2 border-gray-200 flex items-center justify-center mb-4 md:mb-6 text-gray-500">
                  <svg className="w-6 h-6 md:w-8 md:h-8" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M19.916 4.626a.75.75 0 01.208 1.04l-9 13.5a.75.75 0 01-1.154.114l-6-6a.75.75 0 011.06-1.06l5.353 5.353 8.493-12.739a.75.75 0 011.04-.208z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-3 md:mb-4" style={{ color: '#1A1A1A' }}>
                  {solution.title}
                </h3>
                <p className="text-sm md:text-base lg:text-lg text-gray-600 mb-4 md:mb-6 leading-relaxed">
                  {solution.description}
                </p>
                <div className="space-y-2 md:space-y-3">
                  {solution.features.map((feature, i) => (
                    <div key={i} className="flex items-center space-x-2 md:space-x-3">
                      <div className="w-5 h-5 md:w-6 md:h-6 rounded-full border-2 border-gray-200 flex items-center justify-center text-xs md:text-sm font-bold text-gray-500">
                        ✓
                      </div>
                      <span className="text-sm md:text-base text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid Section */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 md:snap-start md:snap-always min-h-screen flex items-center" style={{
        background: 'linear-gradient(180deg, #F5F3F0 0%, #F9F7F4 100%)'
      }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6" style={{ color: '#1A1A1A' }}>
              Alles, was du brauchst – ohne Agentur-Stress.
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto">
              AdSimplify ersetzt teure Agenturen durch smarte Automatisierung<br />
              und glasklare Übersicht.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                title: 'Automatisierte Suchanzeigen',
                description: 'Deine Anzeigen werden von KI erstellt, von Experten optimiert – und in Minuten auf Google veröffentlicht.',
                icon: (
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )
              },
              {
                title: 'Texte von Profis',
                description: 'Wenn du willst, übernimmt ein Experte dein Wording.',
                icon: (
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z" clipRule="evenodd" />
                  </svg>
                )
              },
              {
                title: 'Performance in Echtzeit',
                description: 'Klicks, Anfragen und Kosten live im Dashboard.',
                icon: (
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.375 2.25c-1.035 0-1.875.84-1.875 1.875v15.75c0 1.035.84 1.875 1.875 1.875h.75c1.035 0 1.875-.84 1.875-1.875V4.125c0-1.036-.84-1.875-1.875-1.875h-.75zM9.75 8.625c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-.75a1.875 1.875 0 01-1.875-1.875V8.625zM3 13.125c0-1.036.84-1.875 1.875-1.875h.75c1.036 0 1.875.84 1.875 1.875v6.75c0 1.035-.84 1.875-1.875 1.875h-.75A1.875 1.875 0 013 19.875v-6.75z" />
                  </svg>
                )
              },
              {
                title: 'Klare Budgetkontrolle',
                description: 'Du legst fest, wie viel du ausgibst – kein Cent mehr.',
                icon: (
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z" />
                    <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z" clipRule="evenodd" />
                  </svg>
                )
              },
              {
                title: 'Regionale Optimierung',
                description: 'Zeige deine Anzeigen nur in deinen Bezirken oder Städten.',
                icon: (
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M11.54 22.351l.07.04.028.016a.76.76 0 00.723 0l.028-.015.071-.041a16.975 16.975 0 001.144-.742 19.58 19.58 0 002.683-2.282c1.944-1.99 3.963-4.98 3.963-8.827a8.25 8.25 0 00-16.5 0c0 3.846 2.02 6.837 3.963 8.827a19.58 19.58 0 002.682 2.282 16.975 16.975 0 001.145.742zM12 13.5a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                  </svg>
                )
              },
              {
                title: 'Keine Verträge, keine Laufzeiten',
                description: 'Starte oder stoppe jederzeit – du entscheidest.',
                icon: (
                  <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                  </svg>
                )
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="group bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:scale-105"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl border-2 border-gray-200 flex items-center justify-center mb-4 md:mb-6 text-gray-500">
                  {React.cloneElement(feature.icon, { className: "w-6 h-6 md:w-7 md:h-7" })}
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3" style={{ color: '#1A1A1A' }}>
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - New Design */}
      <section id="how-it-works" className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 relative snap-start snap-always min-h-screen flex items-center" style={{
        background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)'
      }}>
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Column - Headline & CTA */}
            <div className="space-y-6 lg:space-y-8">
              <div className="inline-block">
                <span className="text-xs md:text-sm font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full">
                  So funktioniert's
                </span>
              </div>
              
              <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight" style={{ color: '#1A1A1A' }}>
                So einfach geht's
              </h2>
              
              <p className="text-base md:text-lg lg:text-xl text-gray-600 leading-relaxed">
                In 4 einfachen Schritten zu deiner erfolgreichen Google-Suchanzeige
              </p>
              
              <div>
                <button
                  onClick={() => navigate('/login?mode=register')}
                  className="px-6 md:px-8 py-3 md:py-4 rounded-xl font-semibold text-white text-base md:text-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
                  style={{ backgroundColor: '#0040FF' }}
                >
                  Jetzt starten
                </button>
              </div>
            </div>

            {/* Right Column - Timeline Steps */}
            <div className="relative">
              {/* Vertical Timeline Line - Background */}
              <div className="absolute left-8 top-12 bottom-12 w-0.5 bg-gray-200 hidden md:block"></div>
              
              {/* Vertical Timeline Line - Animated Fill */}
              <div 
                className="absolute left-8 top-12 w-0.5 bg-gradient-to-b from-blue-500 via-blue-400 to-blue-300 hidden md:block transition-all duration-500 ease-out"
                style={{ 
                  height: `${timelineHeight}%`,
                  maxHeight: 'calc(100% - 6rem)'
                }}
              ></div>

              {/* Steps */}
              <div className="space-y-6 md:space-y-8">
                {[
                  {
                    step: '1',
                    title: 'Konto erstellen',
                    description: 'Erstelle dein AdSimplify-Konto und lege dein Ziel fest.',
                    iconBg: 'bg-blue-100',
                    icon: (
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                      </svg>
                    )
                  },
                  {
                    step: '2',
                    title: 'Daten eingeben',
                    description: 'Wähle dein Budget, deine Zielgruppe und deinen Standort – wir übernehmen den Rest.',
                    iconBg: 'bg-yellow-100',
                    icon: (
                      <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M4.755 10.059a7.5 7.5 0 0112.548-3.364l1.903 1.903h-3.183a.75.75 0 100 1.5h4.992a.75.75 0 00.75-.75V4.356a.75.75 0 00-1.5 0v3.18l-1.9-1.9A9 9 0 003.306 9.67a.75.75 0 101.45.388zm15.408 3.352a.75.75 0 00-.919.53 7.5 7.5 0 01-12.548 3.364l-1.902-1.903h3.183a.75.75 0 000-1.5H2.984a.75.75 0 00-.75.75v4.992a.75.75 0 001.5 0v-3.18l1.9 1.9a9 9 0 0015.059-4.035.75.75 0 00-.53-.918z" clipRule="evenodd" />
                      </svg>
                    )
                  },
                  {
                    step: '3',
                    title: 'Kampagne prüfen',
                    description: 'Erhalte deinen Kampagnenvorschlag in Sekunden – inklusive professioneller Anzeigentexte.',
                    iconBg: 'bg-green-100',
                    icon: (
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clipRule="evenodd" />
                      </svg>
                    )
                  },
                  {
                    step: '4',
                    title: 'Aktivieren & Ergebnisse sehen',
                    description: 'Aktiviere deine Kampagne und erscheine genau dort, wo deine Kunden suchen.',
                    iconBg: 'bg-orange-100',
                    icon: (
                      <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M10.464 8.746c.227-.18.497-.311.786-.394v2.795a2.252 2.252 0 01-.786-.393c-.394-.313-.546-.681-.546-1.004 0-.323.152-.691.546-1.004zM12.75 15.662v-2.824c.347.085.664.228.921.421.427.32.579.686.579.991 0 .305-.152.671-.579.991a2.534 2.534 0 01-.921.42z" />
                        <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zM12.75 6a.75.75 0 00-1.5 0v.816a3.836 3.836 0 00-1.72.756c-.712.566-1.112 1.35-1.112 2.178 0 .829.4 1.612 1.113 2.178.502.4 1.102.647 1.719.756v2.978a2.536 2.536 0 01-.921-.421l-.879-.66a.75.75 0 00-.9 1.2l.879.66c.533.4 1.169.645 1.821.75V18a.75.75 0 001.5 0v-.81a4.124 4.124 0 001.821-.749c.745-.559 1.179-1.344 1.179-2.191 0-.847-.434-1.632-1.179-2.191a4.122 4.122 0 00-1.821-.75V8.354c.29.082.559.213.786.393l.415.33a.75.75 0 00.933-1.175l-.415-.33a3.836 3.836 0 00-1.719-.755V6z" clipRule="evenodd" />
                      </svg>
                    )
                  }
                ].map((item, index) => (
                  <div key={index} className="relative flex items-start space-x-4 bg-white rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-12 h-12 md:w-14 md:h-14 rounded-xl ${item.iconBg} flex items-center justify-center`}>
                      {item.icon}
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 pt-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold text-blue-600 bg-blue-50">
                          Schritt {item.step}
                        </span>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold mb-2" style={{ color: '#1A1A1A' }}>
                        {item.title}
                      </h3>
                      <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section - AdSimplify vs. Agentur */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-white snap-start snap-always min-h-screen flex items-center">
        <div className="max-w-6xl mx-auto w-full">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6" style={{ color: '#1A1A1A' }}>
              AdSimplify vs. Agentur
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600">
              Transparenz statt Verträgen. Ergebnisse statt Versprechen.
            </p>
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-2xl overflow-hidden border border-gray-200">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-6 py-4 font-bold text-gray-900 text-base lg:text-lg">Vorteil</th>
                  <th className="text-left px-6 py-4 font-bold text-gray-900 text-base lg:text-lg">AdSimplify</th>
                  <th className="text-left px-6 py-4 font-bold text-gray-900 text-base lg:text-lg">Agentur</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    title: "Keine Bindung",
                    adsimplify: "Monatlich kündbar",
                    agentur: "Meist 3–12 Monate Vertrag"
                  },
                  {
                    title: "Sofort startklar",
                    adsimplify: "Spätestens in 48 Stunden live",
                    agentur: "Lange Onboarding-Prozesse"
                  },
                  {
                    title: "Transparente Kosten",
                    adsimplify: "Budget + 20% Management Gebühr, keine Überraschungen",
                    agentur: "Undurchsichtige Fees, Zusatzkosten"
                  },
                  {
                    title: "Einfache Bedienung",
                    adsimplify: "Kein Fachwissen nötig",
                    agentur: "Kunde muss viel Input liefern"
                  },
                  {
                    title: "Automatische Optimierung",
                    adsimplify: "24/7 KI-Optimierung",
                    agentur: "1× pro Woche oder seltener"
                  },
                  {
                    title: "Direkte Ergebnisse",
                    adsimplify: "Anfragen oft schon ab Tag 1",
                    agentur: "Wochen bis echte Resultate"
                  },
                  {
                    title: "Klares Dashboard",
                    adsimplify: "Einfach, verständlich, ohne Blabla",
                    agentur: "Komplizierte Reports"
                  },
                  {
                    title: "Flexible Anpassungen",
                    adsimplify: "Budget/Anzeige jederzeit ändern",
                    agentur: "Langsame Änderungen + Mehrkosten"
                  },
                  {
                    title: "Geringes Risiko",
                    adsimplify: "Klein anfangen, jederzeit stoppen",
                    agentur: "Hohe Kosten + Vertragsrisiko"
                  },
                  {
                    title: "Support",
                    adsimplify: "Persönlich + schnell",
                    agentur: "Oft wechselnde Ansprechpartner"
                  }
                ].map((item, index) => (
                  <tr key={index} className="border-b border-gray-200 last:border-b-0">
                    <td className="px-6 py-4 font-semibold text-gray-900 text-sm lg:text-base">{item.title}</td>
                    <td className="px-6 py-4 text-gray-700 text-sm lg:text-base">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>{item.adsimplify}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-700 text-sm lg:text-base">
                      <div className="flex items-start space-x-2">
                        <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <span>{item.agentur}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-4">
            {[
              {
                title: "Keine Bindung",
                adsimplify: "Monatlich kündbar",
                agentur: "Meist 3–12 Monate Vertrag"
              },
              {
                title: "Sofort startklar",
                adsimplify: "Spätestens in 48 Stunden live",
                agentur: "Lange Onboarding-Prozesse"
              },
              {
                title: "Transparente Kosten",
                adsimplify: "Budget + 20% Management Gebühr, keine Überraschungen",
                agentur: "Undurchsichtige Fees, Zusatzkosten"
              },
              {
                title: "Einfache Bedienung",
                adsimplify: "Kein Fachwissen nötig",
                agentur: "Kunde muss viel Input liefern"
              },
              {
                title: "Automatische Optimierung",
                adsimplify: "24/7 KI-Optimierung",
                agentur: "1× pro Woche oder seltener"
              },
              {
                title: "Direkte Ergebnisse",
                adsimplify: "Anfragen oft schon ab Tag 1",
                agentur: "Wochen bis echte Resultate"
              },
              {
                title: "Klares Dashboard",
                adsimplify: "Einfach, verständlich, ohne Blabla",
                agentur: "Komplizierte Reports"
              },
              {
                title: "Flexible Anpassungen",
                adsimplify: "Budget/Anzeige jederzeit ändern",
                agentur: "Langsame Änderungen + Mehrkosten"
              },
              {
                title: "Geringes Risiko",
                adsimplify: "Klein anfangen, jederzeit stoppen",
                agentur: "Hohe Kosten + Vertragsrisiko"
              },
              {
                title: "Support",
                adsimplify: "Persönlich + schnell",
                agentur: "Oft wechselnde Ansprechpartner"
              }
            ].map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl border-2 border-gray-200 p-4"
              >
                <h3 className="text-lg font-bold mb-4 text-center" style={{ color: '#1A1A1A' }}>
                  {item.title}
                </h3>
                
                <div className="space-y-3">
                  {/* AdSimplify */}
                  <div className="flex items-start space-x-3 p-4 rounded-xl bg-green-50/50 border border-green-200">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 mb-1">AdSimplify</p>
                      <p className="text-sm text-gray-700">{item.adsimplify}</p>
                    </div>
                  </div>

                  {/* Agentur */}
                  <div className="flex items-start space-x-3 p-4 rounded-xl bg-red-50/50 border border-red-200">
                    <div className="flex-shrink-0 mt-1">
                      <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-gray-900 mb-1">Agentur</p>
                      <p className="text-sm text-gray-700">{item.agentur}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials - New Content */}
      <section id="testimonials" className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 snap-start snap-always min-h-screen flex items-center" style={{
        background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)'
      }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16 lg:mb-20">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6" style={{ color: '#1A1A1A' }}>
              Was Kunden sagen
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-gray-600">
              Echte Erfahrungen von österreichischen Unternehmern
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                quote: "Bei AdSimplify war ich ehrlich überrascht, wie einfach das Ganze sein kann. Wir fühlen uns das erste Mal wirklich betreut und die Anfragen kommen konstant rein, ohne Stress oder komplizierte Abstimmungen. Mittlerweile erhalten wir auch europaweit Aufträge.",
                name: "Yüksel E.",
                role: "Umzüge & Möbeltransport, Österreichweit",
                company: "Einfach Umzug",
                result: "",
                avatar: ""
              },
              {
                quote: "Mit AdSimplify hat das auf Anhieb funktioniert. Wir bekommen jetzt regelmäßig Aufträge und konnten unsere Dienstleistungen sogar nach Niederösterreich erweitern. Für uns als kleines Team ist das ein riesiger Schritt nach vorne.",
                name: "Taner Y.",
                role: "Haustechnik / Installateur, Niederösterreich / Wien",
                company: "Taner Haustechnik",
                result: "",
                avatar: ""
              },
              {
                quote: "Ich kenne mich mit Suchanzeigen überhaupt nicht aus, deshalb war ich skeptisch. Aber schon am ersten Tag kamen die ersten Anfragen rein. AdSimplify übernimmt die komplette Arbeit und ich bekomme täglich neue Anrufe.",
                name: "Orkun A.",
                role: "Finanzberatung / Kreditvermittlung, Österreichweit",
                company: "Orkun Aktuna",
                result: "",
                avatar: ""
              }
            ].map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-4 md:mb-6">
                  <p className="font-bold text-base md:text-lg mb-1" style={{ color: '#1A1A1A' }}>{testimonial.name}</p>
                  <p className="text-xs md:text-sm text-gray-600 mb-3 md:mb-4">{testimonial.role}</p>
                  
                  <div className="flex items-center space-x-1 mb-4 md:mb-6">
                    {[...Array(5)].map((_, i) => (
                      <svg key={i} className="w-4 h-4 md:w-5 md:h-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed text-sm md:text-base lg:text-lg">
                    "{testimonial.quote}"
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 snap-start snap-always min-h-screen flex items-center" style={{
        background: 'linear-gradient(180deg, #F9F7F4 0%, #F5F3F0 100%)'
      }}>
        <div className="max-w-4xl mx-auto w-full">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6" style={{ color: '#1A1A1A' }}>
              Noch Fragen? Wir haben die Antworten.
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "Wie funktioniert AdSimplify genau?",
                answer: "Du gibst uns in 3 Minuten ein paar Infos zu deinem Geschäft. Unsere KI erstellt daraus professionelle Suchanzeigen. Ein Experte prüft alles nochmal – dann geht's live auf Google. Du siehst alle Klicks und Anfragen in Echtzeit im Dashboard."
              },
              {
                question: "Muss ich ein Google-Konto haben?",
                answer: "Nein, du brauchst kein bestehendes Google Ads Konto. Wir kümmern uns um alles – von der Einrichtung bis zur Verwaltung. Du musst dich um nichts Technisches kümmern."
              },
              {
                question: "Wie schnell sehe ich Ergebnisse?",
                answer: "Deine Anzeigen sind innerhalb von 24-48 Stunden live auf Google. Erste Klicks und Anfragen siehst du meist schon in den ersten Tagen. Wie schnell es geht, hängt von deiner Branche und Region ab – aber du siehst alles live im Dashboard."
              },
              {
                question: "Was, wenn ich kein Marketing-Wissen habe?",
                answer: "Das ist genau der Punkt! AdSimplify wurde für Unternehmer ohne Marketing-Kenntnisse gebaut. Du brauchst null Vorwissen – unsere KI und Experten übernehmen die komplizierte Arbeit. Du konzentrierst dich auf dein Geschäft."
              },
              {
                question: "Kann ich mein Budget jederzeit anpassen?",
                answer: "Ja, absolut. Du kannst dein Werbebudget jederzeit erhöhen, senken oder pausieren – mit einem Klick. Keine Mindestlaufzeit, keine Verträge. Du behältst die volle Kontrolle."
              },
              {
                question: "Ist das wirklich meine Anzeige auf Google?",
                answer: "Ja, 100%! Die Anzeigen laufen unter deinem Namen – mit deiner Website und deiner Telefonnummer. Du bist der Absender – wir sind nur im Hintergrund und sorgen dafür, dass alles optimal läuft."
              }
            ].map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-md"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-4 md:px-6 lg:px-8 py-4 md:py-5 lg:py-6 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
                >
                  <span className="text-base md:text-lg font-bold pr-4 md:pr-6 lg:pr-8" style={{ color: '#1A1A1A' }}>
                    {faq.question}
                  </span>
                  <div className={`flex-shrink-0 w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-gray-300 flex items-center justify-center transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`}>
                    <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>
                {openFaqIndex === index && (
                  <div className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5 lg:pb-6 pt-2">
                    <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 md:mt-12 text-center">
            <p className="text-sm md:text-base text-gray-600 mb-3 md:mb-4">
              Hast du noch weitere Fragen?
            </p>
            <a 
              href="mailto:support@adsimplify.at" 
              className="inline-flex items-center space-x-2 px-4 md:px-6 py-2.5 md:py-3 rounded-xl border-2 border-gray-200 font-semibold text-sm md:text-base text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <svg className="w-4 h-4 md:w-5 md:h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
              </svg>
              <span>Kontaktiere unseren Support</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 relative overflow-hidden snap-start snap-always min-h-screen flex items-center">
        <div className="absolute inset-0" style={{
          background: 'linear-gradient(180deg, #F5F3F0 0%, #F9F7F4 100%)'
        }}></div>
        
        <div className="relative max-w-5xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 md:mb-8" style={{ color: '#1A1A1A' }}>
            Bereit, loszulegen?
          </h2>
          <p className="text-lg md:text-xl lg:text-2xl text-gray-600 mb-4 md:mb-6 max-w-3xl mx-auto">
            Erstelle deine erste Google-Suchanzeige in wenigen Minuten.
          </p>
          <p className="text-base md:text-lg lg:text-xl font-semibold text-gray-700 mb-8 md:mb-12">
            Kein Risiko. Keine Verträge. Nur Ergebnisse.
          </p>
          
          <button
            onClick={() => navigate('/login?mode=register')}
            className="w-full md:w-auto px-8 md:px-12 lg:px-16 py-4 md:py-5 lg:py-6 rounded-xl md:rounded-2xl font-bold text-white text-lg md:text-xl lg:text-2xl transition-all duration-300 hover:shadow-2xl hover:scale-105 mb-4 md:mb-6"
            style={{
              background: 'linear-gradient(135deg, #0040FF 0%, #0066FF 100%)'
            }}
          >
            Jetzt starten 🚀
          </button>
          
          <p className="text-xs md:text-sm text-gray-500">
            100 % transparent · monatlich kündbar · DSGVO-konform
          </p>
        </div>
      </section>

      {/* WhatsApp Sticky Button */}
      <a
        href="https://wa.me/436602014784"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 lg:bottom-8 lg:right-8 z-50 w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 bg-green-500 hover:bg-green-600 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 group"
        aria-label="WhatsApp Support"
      >
        <svg 
          className="w-6 h-6 md:w-7 md:h-7 lg:w-9 lg:h-9 text-white" 
          fill="currentColor" 
          viewBox="0 0 24 24"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <div className="absolute right-full mr-2 md:mr-4 whitespace-nowrap bg-gray-900 text-white px-2 md:px-4 py-1 md:py-2 rounded-lg text-xs md:text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          WhatsApp Support
        </div>
      </a>

      {/* Footer */}
      <footer className="py-12 md:py-16 px-4 md:px-6 lg:px-8 border-t border-gray-200 md:snap-start" style={{ backgroundColor: '#F9F7F4' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-3">
              <div className="flex items-center space-x-2">
                <img 
                  src="/logo.png" 
                  alt="AdSimplify Logo" 
                  className="h-6 w-6 md:h-7 md:w-7 object-contain"
                />
                <span className="text-xl md:text-2xl font-bold" style={{ color: '#1A1A1A' }}>
                  AdSimplify
                </span>
              </div>
              <span className="hidden md:inline text-gray-400">·</span>
              <span className="text-xs md:text-sm text-gray-500 text-center md:text-left">
                Google Suchanzeigen für Unternehmer
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 lg:gap-8 text-xs md:text-sm text-gray-600">
              <a href="/datenschutz" className="hover:text-gray-900 transition">Datenschutz</a>
              <a href="/impressum" className="hover:text-gray-900 transition">Impressum</a>
              <a href="/agb" className="hover:text-gray-900 transition">AGB</a>
              <a href="/kontakt" className="hover:text-gray-900 transition">Kontakt</a>
            </div>
          </div>
          <div className="text-center mt-8 md:mt-12 text-xs md:text-sm text-gray-400">
            © 2025 AdSimplify. Made with ❤️ in Österreich.
          </div>
        </div>
      </footer>

      {/* CSS for animations and smooth scroll */}
      <style jsx>{`
        html {
          scroll-behavior: smooth;
        }
        
        /* Scroll snap styling */
        .snap-y {
          scroll-snap-type: y mandatory;
        }
        
        .snap-start {
          scroll-snap-align: start;
        }
        
        .snap-always {
          scroll-snap-stop: always;
        }

        /* Bar chart animations */
        @keyframes growBar {
          from {
            height: 0;
          }
          to {
            height: var(--final-height);
          }
        }

        .chart-bar {
          animation: growBar 1.5s ease-out forwards;
        }
        
        @keyframes fadeInOut {
          0% {
            opacity: 0;
            transform: translateY(20px);
          }
          10% {
            opacity: 1;
            transform: translateY(0);
          }
          90% {
            opacity: 1;
            transform: translateY(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
    </>
  );
};

export default LandingPage;
